/**
 * The shared-list web fallback.
 *
 * Authorised by product-concept §10.1 ("a partner without the app can still add 'bin
 * bags' from a browser") and its objection 3 (scoped to lists only, and load-bearing
 * for the feature actually working in a household). Persona C (§3) is the user: "zero
 * learning curve; the shared surface must work like a normal list even though the
 * other side is running the full ND machinery." So this screen is deliberately NOT
 * the in-app Shopping Mode: no temperature, no capsules, no metaphor, no department
 * grouping, no reorder gesture — a list, full stop.
 *
 * `shoppingModeGroups` (core `lists.ts`) is intentionally unused here, and RULINGS **R58**
 * is CONFIRMED on exactly this question: "the web fallback stays a **flat** add-and-check
 * list. Do not add department grouping... Items stay oldest-first." Its authority is
 * persona C — department-grouped huge-target Shopping Mode is the *in-app* surface for
 * the person actively walking the aisles; a flat list is the smaller, more honest
 * interface for a partner opening a link. This surface is not merely written not to
 * group: the R48 wire (`backend.ts`) carries no department at all, so it *cannot*.
 *
 * ## The status line (RULINGS R48)
 *
 * R48: "the page states which one it is running, because an honest preview beats a false
 * claim of sync." That is the `role="status"` line under the header, and it is the
 * `aria-describedby` target of the add field, so the statement is attached to the action
 * it qualifies rather than floating. All SIX states share ONE presentation — no colour
 * coding, no badge, nothing red — because the sentence is the whole signal. That satisfies
 * design-direction §3's two laws here trivially rather than carefully: "red is never a
 * state", and colour is always redundant with position, icon or label.
 *
 * There were four. `revoked` (RULINGS R68) and `rateLimited` are new because they were being
 * told other states' sentences: a dead link rendered `share.localOnly` ("This phone only for
 * now") on a browser page, and a 429 rendered `sync.failed` ("Could not reach the server...
 * it will try again"), which is false in both halves when the server answered and refused.
 * Adding a state was the fix rather than adding a colour — the sentence is still the whole
 * signal, there is just now a true one for each case.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import type { CopyKey } from '@carn/copy';
import { useTheme } from '../theme/ThemeProvider';
import { useCopy } from '../theme/CopyProvider';
import { useSharedList } from './useSharedList';
import type { SharedListStatus } from './backend';
import { LiteralToggle } from '../marketing/LiteralToggle';

/**
 * Which catalogue key states each backend state. Nothing here claims more than the state
 * supports, and — since a sharing-API critique — nothing here reuses a key written for a
 * different surface:
 *
 *   preview     — `share.localOnly` "This phone only for now. Nothing leaves it yet." NO
 *                 TOKEN IN THE URL, which is the only case that sentence is true for. It used
 *                 to cover the revoked case too, on a page that can be a desktop browser.
 *   loading     — `sync.syncing` "Catching up"
 *   live        — `sync.synced` "Up to date"
 *   revoked     — `sharedList.linkExpired` "This link no longer works. Ask for a new one."
 *                 RULINGS **R68**, verbatim. The add field is REMOVED in this state, not
 *                 merely unhinted: a field that accepts a word and drops it is worse than no
 *                 field, and this is persona C's only surface (product-concept §3, "zero
 *                 learning curve").
 *   rateLimited — `sharedList.rateLimited`. The server answered and refused; the write is
 *                 queued and will be replayed. Distinct from `degraded` because "could not
 *                 reach the server" is false here.
 *   degraded    — `sync.failed` "Could not reach the server. Your work is safe here; it will
 *                 try again." True in both halves: the write is in localStorage, and
 *                 `ShareLinkBackend`'s outbox really does replay it.
 */
const STATUS_COPY: Record<SharedListStatus, CopyKey> = {
  preview: 'share.localOnly',
  loading: 'sync.syncing',
  live: 'sync.synced',
  revoked: 'sharedList.linkExpired',
  rateLimited: 'sharedList.rateLimited',
  degraded: 'sync.failed',
};

export function SharedListPage({
  token,
  previewListId,
}: {
  token: string | null;
  previewListId: string;
}) {
  const theme = useTheme();
  const { t } = useCopy();
  const { loaded, status, listName, unchecked, checked, complete, addItem, setChecked, notice } =
    useSharedList({ token, previewListId });
  const [draft, setDraft] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const statusId = `${fieldId}-status`;
  const hintId = `${fieldId}-hint`;
  // A revoked link accepts nothing (R68), so the whole interactive surface goes: no add
  // field, no checkboxes, no stale snapshot presented as a list. What is left is the one
  // sentence that explains why, which is exactly what R68's authority note asks for.
  const dead = status === 'revoked';
  // The add hint claims the item reaches "everyone who has this link". That is only true
  // when a link is actually behind the page, so in preview mode it is not rendered and
  // the status line is the field's whole description.
  const showAddHint = status !== 'preview';

  // A capability URL must not end up in a search index (it is a bearer credential, and
  // the page behind it is somebody's household list). The marketing route keeps its
  // default indexability; only the tokenised list route opts out.
  useEffect(() => {
    if (!token || typeof document === 'undefined') return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [token]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    setDraft('');
    inputRef.current?.focus();
    // THE ANNOUNCEMENT WAITS FOR THE ANSWER. It used to fire synchronously, one line after
    // `void addItem(name)`, so a link revoked between the submit and the response announced
    // "bin bags added." for an item that was refused and never queued. `addItem` now reports
    // whether it was accepted, and the status line says what happened either way.
    void addItem(name).then((accepted) => {
      if (accepted) setAnnouncement(t('sharedList.added', { item: name }));
    });
  };

  const c = theme.colours;
  const s = theme.space;

  return (
    <main
      style={{
        minHeight: '100vh',
        background: c.background,
        color: c.text,
        fontFamily: theme.type.family,
        padding: s.screenPadding,
        maxWidth: 560,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: s.lg,
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: s.md }}>
        <div>
          <h1 style={{ ...typeStyle(theme.type.title), color: c.text, margin: 0 }}>
            {t('shopping.title')}
          </h1>
          {/* The list's own name, when the server sent one. A partner opening a link needs
              to know which list they are adding to (product-concept §10.1); it is user
              content, not copy, so it carries no catalogue key. */}
          {listName !== undefined && (
            <p style={{ ...typeStyle(theme.type.label), color: c.textSecondary, margin: `${s.xs}px 0 0` }}>
              {listName}
            </p>
          )}
          <p style={{ ...typeStyle(theme.type.body), color: c.textSecondary, margin: `${s.xs}px 0 0` }}>
            {t('sharedList.pageIntro')}
          </p>
        </div>
        <LiteralToggle />
      </header>

      {/* R48's "the page states which one it is running". One presentation for all four
          states: the sentence is the signal, never a colour. */}
      <p
        id={statusId}
        role="status"
        style={{
          ...typeStyle(theme.type.body),
          color: c.textSecondary,
          background: c.surface,
          border: `${s.hairline}px solid ${c.border}`,
          borderRadius: s.radius.md,
          padding: s.md,
          margin: 0,
        }}
      >
        {t(STATUS_COPY[status])}
      </p>

      {/* R68: a dead link renders no add field. Everything below is suppressed too — see
          `dead`. A form that takes a word and silently drops it is the failure this state
          exists to end. */}
      {!dead && (
        <form onSubmit={onSubmit} style={{ display: 'flex', gap: s.sm, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: s.xs }}>
            <label htmlFor={fieldId} style={{ ...typeStyle(theme.type.label), color: c.textSecondary }}>
              {t('sharedList.addLabel')}
            </label>
            <input
              id={fieldId}
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('sharedList.addPlaceholder')}
              aria-describedby={showAddHint ? `${statusId} ${hintId}` : statusId}
              style={{
                ...typeStyle(theme.type.body),
                minHeight: s.minTarget,
                padding: `0 ${s.md}px`,
                borderRadius: s.radius.md,
                border: `${s.hairline}px solid ${c.borderStrong}`,
                background: c.surface,
                color: c.text,
                boxSizing: 'border-box',
              }}
            />
            {showAddHint && (
              <span id={hintId} style={{ ...typeStyle(theme.type.quiet), color: c.textTertiary }}>
                {t('sharedList.addHint')}
              </span>
            )}
          </div>
          <button
            type="submit"
            style={primaryButtonStyle(theme)}
            aria-label={t('sharedList.addSubmit')}
          >
            {t('sharedList.addSubmit')}
          </button>
        </form>
      )}

      {/* Screen-reader-only live region: the confirmation a sighted user sees as a
          momentary state change (design-direction §5's announcement discipline). */}
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {announcement}
      </span>

      {/* A notice is a fact about ONE item, not a state of the page, so it renders as its own
          line and is announced. Today there is exactly one kind: an item the household took
          off the list after this visitor had queued it (`sharedList.itemWithdrawn`). It is
          visible as well as announced — a sighted visitor needs the same explanation, and a
          `role="status"` sentence is the whole signal, with no colour doing any work
          (design-direction §3). */}
      {notice && (
        <p
          role="status"
          aria-live="polite"
          style={{
            ...typeStyle(theme.type.body),
            color: c.textSecondary,
            background: c.surface,
            border: `${s.hairline}px solid ${c.border}`,
            borderRadius: s.radius.md,
            padding: s.md,
            margin: 0,
          }}
        >
          {t('sharedList.itemWithdrawn', { item: notice.itemName })}
        </p>
      )}

      {!dead && complete && (
        <p
          role="status"
          style={{
            ...typeStyle(theme.type.body),
            color: c.temperature.closed.mark,
            background: c.temperature.closed.surface,
            border: `${s.hairline}px solid ${c.temperature.closed.border}`,
            borderRadius: s.radius.md,
            padding: s.md,
            margin: 0,
          }}
        >
          {t('shopping.complete')}
        </p>
      )}

      {/* The list itself is suppressed on a dead link: whatever is in this browser's
          snapshot is a record of a list this visitor can no longer reach, and rendering it
          with live checkboxes would be the same false claim of sync one layer down. */}
      {!dead && (
        <section aria-label={t('shopping.title')}>
          {loaded && unchecked.length === 0 && checked.length === 0 && (
            <p style={{ ...typeStyle(theme.type.body), color: c.textSecondary }}>{t('shopping.empty')}</p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: s.cardGap }}>
            {unchecked.map((item) => (
              <ItemRow key={item.id} name={item.name} checked={false} onToggle={(v) => setChecked(item.id, v)} />
            ))}
          </ul>
        </section>
      )}

      {!dead && checked.length > 0 && (
        <details>
          <summary
            style={{
              ...typeStyle(theme.type.quiet),
              color: c.textSecondary,
              cursor: 'pointer',
              minHeight: s.minTarget,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {t('shopping.checkedRow', { count: checked.length })}
          </summary>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: s.cardGap }}>
            {checked.map((item) => (
              <ItemRow key={item.id} name={item.name} checked onToggle={(v) => setChecked(item.id, v)} />
            ))}
          </ul>
        </details>
      )}
    </main>
  );
}

function ItemRow({
  name,
  checked,
  onToggle,
}: {
  name: string;
  checked: boolean;
  onToggle: (value: boolean) => void;
}) {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;
  const id = useId();

  return (
    <li>
      <label
        htmlFor={id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: s.md,
          minHeight: s.minTarget,
          padding: `0 ${s.cardPadding}px`,
          background: c.surface,
          border: `${s.hairline}px solid ${c.border}`,
          borderRadius: s.radius.md,
          cursor: 'pointer',
        }}
      >
        {/* THE ACCESSIBLE NAME ALWAYS NAMES THE ITEM, IN BOTH STATES.
            This was `aria-label={checked ? t('shopping.uncheck') : name}`. An `aria-label` on
            the `<input>` overrides the associated `<label>`, so every checked box announced
            the same words — "Put it back on the list, checked, checkbox" — and a VoiceOver
            user inside the "N in the trolley" disclosure could not tell milk from bin bags,
            let alone uncheck the right row. The unchecked rows had the mirror problem in a
            milder form: the name was announced and the verb was not.
            `sharedList.checkItem`/`sharedList.uncheckItem` are slotted keys carrying both.
            The `title=` hint that used to sit on the row is gone with it: hover-only text is
            unreliable for touch and for assistive technology, and the accessible name now
            says the same thing where it can actually be heard. */}
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={
            checked
              ? t('sharedList.uncheckItem', { item: name })
              : t('sharedList.checkItem', { item: name })
          }
          style={{ width: 24, height: 24, flexShrink: 0 }}
        />
        <span
          style={{
            ...typeStyle(theme.type.body),
            color: checked ? c.textTertiary : c.text,
            textDecoration: checked ? 'line-through' : 'none',
          }}
        >
          {name}
        </span>
      </label>
    </li>
  );
}

function typeStyle(style: {
  fontSize: number;
  lineHeight: number;
  fontWeight: string;
  letterSpacing: number;
}): React.CSSProperties {
  return {
    fontSize: style.fontSize,
    lineHeight: `${style.lineHeight}px`,
    fontWeight: style.fontWeight as React.CSSProperties['fontWeight'],
    letterSpacing: style.letterSpacing,
  };
}

function primaryButtonStyle(theme: ReturnType<typeof useTheme>): React.CSSProperties {
  return {
    ...typeStyle(theme.type.body),
    minHeight: theme.space.minTarget,
    minWidth: theme.space.minTarget,
    padding: `0 ${theme.space.lg}px`,
    borderRadius: theme.space.radius.md,
    border: 'none',
    background: theme.colours.accent,
    color: theme.colours.onAccent,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
